import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTATConfigurations } from './useTATConfigurations';
import { useBranchContext } from "@/hooks/useBranchContext";
import { useOrganizationContext } from "@/hooks/useOrganizationContext";

interface TATTimerData {
  countdown: string;
  status: 'green' | 'yellow' | 'red' | 'exceeded';
  activityName: string;
  elapsedTime: string;
}

export const usePatientTATTimer = (patientId: string) => {
  const [tatData, setTatData] = useState<TATTimerData | null>(null);
  const [mdCompleted, setMdCompleted] = useState<boolean | null>(null);
  const { data: tatConfigs } = useTATConfigurations();
  const { currentBranchId } = useBranchContext();
  const { organizationId } = useOrganizationContext();

  // Check if MD (Marked for Discharge) is completed - timer should only run after MD
  useEffect(() => {
    const checkMDStatus = async () => {
      if (currentBranchId == null || !organizationId) return;
      const { data: mdActivity } = await supabase
        .from('activities')
        .select('status')
        .eq('patient_id', patientId)
        .eq('workflow_step', 'MD')
        .eq('branch_id', currentBranchId)
        .eq('organization_id', organizationId)
        .maybeSingle();
      
      setMdCompleted(mdActivity?.status === 'completed');
    };

    checkMDStatus();
    
    // Subscribe to MD changes
    const channel = supabase
      .channel(`md-status-${patientId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'activities',
          filter: `patient_id=eq.${patientId}`,
        },
        (payload) => {
          if (payload.new?.workflow_step === 'MD') {
            setMdCompleted(payload.new?.status === 'completed');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [patientId, currentBranchId, organizationId]);

  useEffect(() => {
    // Don't calculate TAT until we know MD status, or if MD is not completed
    if (currentBranchId == null || !organizationId || mdCompleted === null || mdCompleted === false || !tatConfigs) {
      if (mdCompleted === false) {
        // MD not completed - show waiting state
        setTatData({
          countdown: '00:00',
          status: 'green',
          activityName: 'Awaiting MD',
          elapsedTime: '00:00:00',
        });
      }
      return;
    }

    const calculateTAT = async () => {
      // Get all in-progress activities to find the one with maximum elapsed time
      const { data: activities, error } = await supabase
        .from('activities')
        .select('*')
        .eq('patient_id', patientId)
        .eq('status', 'in-progress')
        .eq('branch_id', currentBranchId)
        .eq('organization_id', organizationId)
        .order('display_order', { ascending: false });

      if (error || !activities || activities.length === 0) {
        // Check if there are pending activities for this patient
        const { data: pendingActivities } = await supabase
          .from('activities')
          .select('name')
          .eq('patient_id', patientId)
          .eq('status', 'pending')
          .eq('branch_id', currentBranchId)
          .eq('organization_id', organizationId)
          .limit(1);
        
        // If there are pending activities, show 00:00:00 with green status
        if (pendingActivities && pendingActivities.length > 0) {
          setTatData({
            countdown: '00:00',
            status: 'green',
            activityName: 'Pending',
            elapsedTime: '00:00:00',
          });
        } else {
          setTatData(null);
        }
        return;
      }

      // Find the activity with maximum elapsed time
      let maxElapsedActivity = activities[0];
      let maxElapsedMs = 0;

      for (const act of activities) {
        const startTimestamp = act.assigned_at || act.started_at;
        if (startTimestamp) {
          const elapsedMs = new Date().getTime() - new Date(startTimestamp).getTime();
          if (elapsedMs > maxElapsedMs) {
            maxElapsedMs = elapsedMs;
            maxElapsedActivity = act;
          }
        }
      }

      const activity = maxElapsedActivity;
      
      // Get start time directly from activity timestamps
      const startTimestamp = activity.assigned_at || activity.started_at;
      
      if (!startTimestamp) {
        setTatData(null);
        return;
      }

      // Calculate elapsed time
      const start = new Date(startTimestamp);
      const now = new Date();
      const elapsedMinutes = Math.floor((now.getTime() - start.getTime()) / 60000);

      // Calculate elapsed time in HH:MM:SS format
      const elapsedMs = now.getTime() - start.getTime();
      const elapsedHours = Math.floor(elapsedMs / (1000 * 60 * 60));
      const elapsedMins = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
      const elapsedSecs = Math.floor((elapsedMs % (1000 * 60)) / 1000);
      const elapsedTime = `${elapsedHours.toString().padStart(2, '0')}:${elapsedMins.toString().padStart(2, '0')}:${elapsedSecs.toString().padStart(2, '0')}`;

      // Find TAT configuration from cached data (activity-based)
      const tatConfig = activity.workflow_step ? tatConfigs.find(
        cfg => cfg.workflow_step === activity.workflow_step
      ) : null;

      // If no TAT config, return elapsed time with duration-based status
      if (!tatConfig) {
        // Determine status based on elapsed time duration
        let status: 'green' | 'yellow' | 'red' | 'exceeded' = 'green';
        if (elapsedMinutes > 120) {
          status = 'exceeded'; // > 2 hours
        } else if (elapsedMinutes > 60) {
          status = 'red'; // > 1 hour
        } else if (elapsedMinutes > 30) {
          status = 'yellow'; // > 30 minutes
        }
        
        setTatData({
          countdown: '00:00',
          status,
          activityName: activity.name,
          elapsedTime,
        });
        return;
      }

      const allowedMinutes = tatConfig.allowed_tat_minutes;
      const level1Threshold = Math.floor(allowedMinutes * (tatConfig.level_1_threshold_percentage / 100));
      const level2Threshold = Math.floor(allowedMinutes * (tatConfig.level_2_threshold_percentage / 100));
      const gracePeriod = tatConfig.grace_period_minutes;

      // Calculate remaining time
      const remainingMinutes = allowedMinutes - elapsedMinutes;
      const isNegative = remainingMinutes < 0;
      const absRemaining = Math.abs(remainingMinutes);
      const hours = Math.floor(absRemaining / 60);
      const mins = absRemaining % 60;
      const countdown = `${isNegative ? '-' : ''}${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

      // Determine status
      let status: 'green' | 'yellow' | 'red' | 'exceeded' = 'green';
      if (elapsedMinutes > level2Threshold + gracePeriod) {
        status = 'exceeded';
      } else if (elapsedMinutes > level2Threshold) {
        status = 'red';
      } else if (elapsedMinutes > level1Threshold) {
        status = 'yellow';
      }

      setTatData({
        countdown,
        status,
        activityName: activity.name,
        elapsedTime,
      });
    };

    calculateTAT();
    const interval = setInterval(calculateTAT, 2000); // Update every 2 seconds to reduce CPU load

    return () => clearInterval(interval);
  }, [patientId, tatConfigs, mdCompleted, currentBranchId, organizationId]);

  return tatData;
};
